import { CommonModule } from '@angular/common';
import { Component, computed, DestroyRef, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
	catchError, debounceTime, distinctUntilChanged, map, Observable, of, skip,
	Subject, Subscription, switchMap, tap, throwError, firstValueFrom
} from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgSelectModule } from '@ng-select/ng-select';

import {
	TablePlaceholderComponent,
	ErrorComponent, EmptyComponent, SpinnerComponent,
	LeagueIconComponent, TeamIconComponent,
	PlayerIconComponent, CoachIconComponent,
	ViewIconComponent,
	StatusIconComponent, StatusTypes,
	DocumentActionsComponent,
} from '@app/shared/components';
import { ApprovalsService, GlobalFiltersService, LookupService, TeamsService } from '@app/shared/services';
import {
	LookupView, TeamListViewModel,
	PagedModel, EntityDocumentViewModel, CoachListViewModel,
	PlayerListViewModel, DocumentTypeLookupView
} from '@app/shared/models';
import {
	LookupCodes, Messages,
	DocumentStatusCodes, DocumentSubjectCodes, LeagueDocumentTypeCodes,
	DocumentApprovalsSortCodes, DocumentApprovalsSortCodesLabels,
} from '@app/constants';
import {
	DateRange, DateRangeDropdownComponent
} from '@app/shared/components/date-range-dropdown/date-range-dropdown.component';

const EmptyId = 0;

interface ApprovalsFilters {
	leagueID: number | null;
	teamID: number | null;
	teamPlayerID: number | null,
	teamCoachID: number | null,
	documentStatusIDs: number[] | null,
	submissionDateFrom: string | null,
	submissionDateTo: string | null,
	submittedByUserID: number | null,
	documentSubjectIDs: number[] | null,
	documentTypeIDs: number[],
	searchText: string,
	sortField: string,
	sortDirection: 'asc' | 'desc';
}

const AllLeagues: LookupView = {
	id: EmptyId,
	name: 'All Leagues',
} as LookupView;

const AllTeams: TeamListViewModel = {
	teamId: EmptyId,
	teamName: 'All Teams',
} as TeamListViewModel;

interface TeamUserOption {
	label: string;
	playerId: number | null;
	coachId: number | null;
}

const AllPlayersAndCoaches: TeamUserOption = {
	label: 'All Players and Coaches',
	playerId: EmptyId,
	coachId: EmptyId,
}

const AllPlayers: TeamUserOption = {
	label: 'All Players',
	playerId: EmptyId,
	coachId: null,
}

const AllCoaches: TeamUserOption = {
	label: 'All Coaches',
	playerId: null,
	coachId: EmptyId,
};

const AllSubmitterUsers: LookupView = {
	id: EmptyId,
	name: 'Submitted by Anyone',
} as LookupView;

interface DocumentTypeSelectModel {
	id: number;
	name: string;
	groupId: number;
	selected: boolean;
}

interface GroupedDocumentType {
	groupLabel: string;
	groupId: number;
	selected: boolean;
	documents: DocumentTypeSelectModel[];
}

interface DocumentStatusSelectModel {
	id: number;
	name: string;
}

const AllDocumentTypes: GroupedDocumentType = {
	groupId: -1,
	groupLabel: 'All Document Types',
	selected: true,
	documents: []
};

const AllDocumentStatuses: DocumentStatusSelectModel = {
	id: -1,
	name: 'All Statuses',
};

@Component({
	selector: 'app-approvals-page',
	templateUrl: './approvals.page.html',
	styleUrl: './approvals.page.scss',
	standalone: true,
	imports: [
		CommonModule, RouterModule, FormsModule,
		NgbTooltipModule, InfiniteScrollModule, NgSelectModule,
		TablePlaceholderComponent, ErrorComponent, EmptyComponent, SpinnerComponent,
		LeagueIconComponent, TeamIconComponent, PlayerIconComponent, CoachIconComponent,
		ViewIconComponent, StatusIconComponent,
		DocumentActionsComponent, DateRangeDropdownComponent, NgbDropdownModule,
	],
})
export class ApprovalsPage implements OnInit, OnDestroy {
	// internal state
	filters: ApprovalsFilters = {
		leagueID: EmptyId,
		teamID: EmptyId,
		teamPlayerID: null,
		teamCoachID: null,
		documentStatusIDs: [DocumentStatusCodes.InProgress],
		submissionDateFrom: null,
		submissionDateTo: null,
		submittedByUserID: EmptyId,
		documentSubjectIDs: null,
		documentTypeIDs: [AllDocumentTypes.groupId], // -1 means "All Document Types"
		searchText: '',
		sortField: DocumentApprovalsSortCodes.LeagueName,
		sortDirection: 'asc',
	};
	searchChange = new Subject<string>();

	get isFiltersEmpty() {
		return Object.values(this.filters).every((f) => f === null);
	}

	approvals: EntityDocumentViewModel[] = [];
	approvalsLoading = signal(false);
	approvalsError = signal(false);
	approvalsTotal: number | null = null;
	currentPage = 1;

	leaguesLookup: LookupView[] = [AllLeagues];
	leaguesLookupLoading = true;

	teams: TeamListViewModel[] = [AllTeams];
	teamsLoading = signal(false);
	teamsError = signal(false);
	isTeamsDisabled = computed(() => this.teamsLoading() || this.approvalsLoading() || !this.filters.leagueID);

	users: TeamUserOption[] | null = null;
	selectedUser: TeamUserOption = AllPlayersAndCoaches;
	usersLoading = false;
	usersError = false;

	submitterUsers: LookupView[] = [];
	submitterUsersLoading = true;
	submitterUsersError = false;

	private leagueYearSubscription: Subscription | null = null;

	// services
	private toastrService = inject(ToastrService);
	private globalFiltersService = inject(GlobalFiltersService);
	private lookupService = inject(LookupService);
	private approvalsService = inject(ApprovalsService);
	private teamsService = inject(TeamsService);
	private destroyRef = inject(DestroyRef);

	// lookups
	documentStatusesLookup$!: Observable<DocumentStatusSelectModel[]>;
	documentStatusesFlat: DocumentStatusSelectModel[] = [];
	documentStatusesLookupLoading = true;

	documentTypesLookup$!: Observable<GroupedDocumentType[]>;
	documentTypesFlat: GroupedDocumentType[] = [];
	documentTypesLookupLoading = true;

	// constants
	DocumentStatusCodes = DocumentStatusCodes;
	DocumentSubjectCodes = DocumentSubjectCodes;
	LeagueDocumentTypeCodes = LeagueDocumentTypeCodes;
	// mapping from documentsubjectcodes to StatusTypes
	DocumentSubjectStatusTypeMapping: { [key: number]: StatusTypes } = {
		[DocumentSubjectCodes.PlayerDocument]: StatusTypes.Document,
		[DocumentSubjectCodes.CoachDocument]: StatusTypes.Document,
		[DocumentSubjectCodes.TeamDocument]: StatusTypes.Document,
		[DocumentSubjectCodes.LeagueDocument]: StatusTypes.AffiliationApp,
		[DocumentSubjectCodes.OtherDocument]: StatusTypes.Document,
	};
	DocumentApprovalsSortCodes = DocumentApprovalsSortCodes;
	sortFields = Object.values(DocumentApprovalsSortCodes);
	DocumentApprovalsSortCodesLabels = DocumentApprovalsSortCodesLabels;
	emptyId = signal<number>(EmptyId);
	allPlayersAndCoaches = signal(AllPlayersAndCoaches);

	constructor() {
		this.initializeLookups();
	}

	ngOnInit() {
		this.leagueYearSubscription = this.globalFiltersService.getLeagueYear$()
			.pipe(skip(1)) // skip the first value, which is the initial value
			.subscribe(() => this.onLoadApprovals(true));
		this.onLoadApprovals(true);
		this.connectSearch();

		// scroll to top
		window.scrollTo(0, 0);
	}

	ngOnDestroy() {
		this.leagueYearSubscription?.unsubscribe();
	}

	private initializeLookups() {
		firstValueFrom(
			this.lookupService.getLookup<LookupView>(LookupCodes.Leagues)
				.pipe(
					tap((leagues) => {
						setTimeout(() => this.leaguesLookupLoading = false, 0);
						this.leaguesLookup = [AllLeagues, ...leagues];
					}),
					catchError(err => {
						setTimeout(() => this.leaguesLookupLoading = false, 0);
						return this.showLookupErrorToast('Leagues', err);
					})
				)
		);

		this.documentStatusesLookup$ = this.lookupService.getLookup<LookupView>(LookupCodes.DocumentStatuses)
			.pipe(
				tap(() => setTimeout(() => this.documentStatusesLookupLoading = false, 0)),
				map((statuses: LookupView[]) => {
					// Add synthetic "All Statuses" option at the top
					const allStatuses = [AllDocumentStatuses];
					return allStatuses.concat(
						statuses.map(status => ({
							id: status.id,
							name: status.name,
						}))
					);
				}),
				tap(statuses => this.documentStatusesFlat = statuses),
				catchError(err => {
					setTimeout(() => this.documentStatusesLookupLoading = false, 0);
					return this.showLookupErrorToast('Document Statuses', err);
				})
			);

		this.documentTypesLookup$ = this.lookupService.getLookup<DocumentTypeLookupView>(LookupCodes.DocumentTypes)
			.pipe(
				map((documentTypes: DocumentTypeLookupView[]) => this.groupDocumentTypesBySubjectName(documentTypes)),
				tap(documentTypes => this.documentTypesFlat = documentTypes),
				tap(() => setTimeout(() => this.documentTypesLookupLoading = false, 0)),
				catchError(err => {
					setTimeout(() => this.documentTypesLookupLoading = false, 0);
					return this.showLookupErrorToast('Document Types', err);
				})
			);

		firstValueFrom(
			this.lookupService.getLookup<LookupView>(LookupCodes.Employees)
				.pipe(
					tap((users) => {
						setTimeout(() => this.submitterUsersLoading = false, 0);
						this.submitterUsers = [AllSubmitterUsers, ...users];
					}),
					catchError(err => {
						setTimeout(() => this.submitterUsersLoading = false, 0);
						return this.showLookupErrorToast('Submitter Users', err);
					})
				)
		);
	}

	private showLookupErrorToast(lookupName: string, error: any) {
		this.toastrService.error(
			Messages.ErrorRetry,
			`Failed to Load '${lookupName}'`,
		);
		return throwError(() => error);
	}

	private resetApprovalsResults() {
		this.currentPage = 1;
		this.approvals = [];
		this.approvalsTotal = null;
	}

	private connectSearch() {
		return this.searchChange
			.pipe(
				debounceTime(500),
				distinctUntilChanged(),
				switchMap(query => {
					// save search query
					this.filters.searchText = query;

					// reset results and page
					this.resetApprovalsResults();

					// show loading indicator
					this.approvalsLoading.set(true);
					this.approvalsError.set(false);

					return this.approvalsService.getApprovals$(
						this.filters.leagueID,
						this.filters.teamID,
						this.filters.teamPlayerID,
						this.filters.teamCoachID,
						this.filters.documentStatusIDs?.includes(AllDocumentStatuses.id) ? null : this.filters.documentStatusIDs,
						this.filters.submissionDateFrom,
						this.filters.submissionDateTo,
						this.filters.submittedByUserID,
						this.filters.documentTypeIDs.includes(AllDocumentTypes.groupId) ? null : this.filters.documentTypeIDs,
						this.filters.searchText,
						this.filters.sortField,
						this.filters.sortDirection,
						this.currentPage,
					).pipe(
						// handle fetch errors
						catchError((error) => {
							console.error('error searching approvals', error);
							this.approvalsError.set(true);
							this.approvalsLoading.set(false);
							return of(null);
						})
					);
				}),
				takeUntilDestroyed(this.destroyRef)
			)
			.subscribe(response => {
				this.processApprovalsPageData(response);
				this.approvalsLoading.set(false);
			});
	}

	async onLoadApprovals(reset: boolean) {
		if (reset) {
			this.resetApprovalsResults();
		}

		try {
			this.approvalsLoading.set(true);
			this.approvalsError.set(false);

			const response = await this.approvalsService.getApprovals(
				this.filters.leagueID,
				this.filters.teamID,
				this.filters.teamPlayerID,
				this.filters.teamCoachID,
				this.filters.documentStatusIDs?.includes(AllDocumentStatuses.id) ? null : this.filters.documentStatusIDs,
				this.filters.submissionDateFrom,
				this.filters.submissionDateTo,
				this.filters.submittedByUserID,
				this.filters.documentTypeIDs.includes(AllDocumentTypes.groupId) ? null : this.filters.documentTypeIDs,
				this.filters.searchText,
				this.filters.sortField,
				this.filters.sortDirection,
				this.currentPage,
			);

			this.processApprovalsPageData(response);
		} catch (error) {
			this.approvalsError.set(true);
		} finally {
			this.approvalsLoading.set(false);
		}
	}

	private processApprovalsPageData(response: PagedModel<EntityDocumentViewModel> | null) {
		if (!response) {
			return;
		}

		if (!this.approvals) {
			this.approvals = [];
		}

		this.approvals.push(...response.pageData.filter(a => this.approvals.findIndex(aa => aa.documentSubmissionID === a.documentSubmissionID) === -1));
		this.approvalsTotal = response.totalRecords || this.approvals.length;
		if (response.pageData.length > 0) {
			this.currentPage++;
		}
	}

	onApprovedOrRejected(approval: EntityDocumentViewModel) {
		const index = this.approvals.findIndex(a => a.documentSubmissionID === approval.documentSubmissionID);
		if (index !== -1) {
			this.approvals.splice(index, 1);
		}
	}

	async onLeagueChange() {
		this.clearTeamFilter();
		this.loadTeamsForSelectedLeague();
		this.onLoadApprovals(true);
	}

	async onTeamChange() {
		this.clearPlayerAndCoachFilters();
		this.loadPlayersCoachesForSelectedTeam();
		this.onLoadApprovals(true);
	}

	private clearTeamFilter() {
		this.teams = [AllTeams];
		this.filters.teamID = AllTeams.teamId;
		this.clearPlayerAndCoachFilters();
	}

	private clearPlayerAndCoachFilters() {
		this.users = [AllPlayersAndCoaches];
		this.filters.teamPlayerID = null;
		this.filters.teamCoachID = null;
		this.selectedUser = AllPlayersAndCoaches;
	}

	private async loadTeamsForSelectedLeague() {
		if (this.teamsLoading()) return;

		this.teams = [AllTeams];

		if (!this.filters.leagueID) {
			this.clearTeamFilter();
			return;
		}

		try {
			this.teamsLoading.set(true);
			this.teamsError.set(false);

			const teams = await this.teamsService.getTournamentTeams(this.filters.leagueID);
			this.teams = [AllTeams, ...teams];
		} catch (error) {
			this.teamsError.set(true);
		} finally {
			this.teamsLoading.set(false);
		}
	}

	private async loadPlayersCoachesForSelectedTeam() {
		if (this.usersLoading) return;

		this.users = [AllPlayersAndCoaches];

		if (!this.filters.teamID) {
			this.clearPlayerAndCoachFilters();
			return;
		}

		this.usersLoading = true;
		this.usersError = false;

		try {
			const [players, coaches] = await Promise.all([
				this.teamsService.getTeamPlayers(this.filters.teamID, false),
				this.teamsService.getTeamCoaches(this.filters.teamID),
			]);

			this.users = [
				AllPlayersAndCoaches,

				...(players?.length ? [AllPlayers] : []),
				...players.map((p: PlayerListViewModel) => ({
					label: p.fullName,
					playerId: p.playerId,
					coachId: null,
				})),

				...(coaches?.length ? [AllCoaches] : []),
				...coaches.map((c: CoachListViewModel) => ({
					label: c.fullName,
					playerId: null,
					coachId: c.coachId,
				})),
			];
		} catch (error) {
			this.usersError = true;
			this.users = null;
		} finally {
			this.usersLoading = false;
		}
	}

	async dateRangeChange(event: DateRange) {
		this.filters.submissionDateFrom = event.start;
		this.filters.submissionDateTo = event.end;
		this.onLoadApprovals(true);
	}

	async onUserSelect(user: TeamUserOption) {
		console.log('Selected user:', user);

		this.selectedUser = user;
		this.filters.teamPlayerID = user?.playerId ?? null;
		this.filters.teamCoachID = user?.coachId ?? null;
		this.onLoadApprovals(true);
	}

	private groupDocumentTypesBySubjectName(documentTypes: DocumentTypeLookupView[]): GroupedDocumentType[] {
		const groupedMap = new Map<number, GroupedDocumentType>();

		for (const doc of documentTypes) {
			const groupId = doc.documentSubjectID ?? -999;
			const groupLabel = doc.documentSubjectName || 'Other';

			if (!groupedMap.has(groupId)) {
				groupedMap.set(groupId, {
					groupId,
					groupLabel,
					selected: false,
					documents: []
				});
			}

			groupedMap.get(groupId)!.documents.push({
				id: doc.id,
				name: doc.name,
				selected: false,
				groupId,
			});
		}

		// Add synthetic "All Document Types" group at the top
		return [
			AllDocumentTypes,
			...Array.from(groupedMap.values())
		];
	}

	async onDocumentStatusesChange(id: number) {
		// Initialize array if null
		if (!this.filters.documentStatusIDs) {
			this.filters.documentStatusIDs = [];
		}

		// If clicked "All Statuses" or nothing selected — reset to only All
		if (id === AllDocumentStatuses.id || this.filters.documentStatusIDs.length === 0) {
			this.filters.documentStatusIDs = [AllDocumentStatuses.id];
		} else {
			// Remove AllStatuses if it's currently selected
			this.filters.documentStatusIDs = this.filters.documentStatusIDs.filter(i => i !== AllDocumentStatuses.id);

			const index = this.filters.documentStatusIDs.indexOf(id);

			if (index > -1) {
				// Deselect clicked ID
				this.filters.documentStatusIDs.splice(index, 1);
			} else {
				// Select new ID
				this.filters.documentStatusIDs.push(id);
			}

			// If after deselect nothing remains, fallback to All
			if (this.filters.documentStatusIDs.length === 0) {
				this.filters.documentStatusIDs = [AllDocumentStatuses.id];
			}
		}

		await this.onLoadApprovals(true);
	}

	documentStatusesLabel(): string {
		const selectedIds: number[] = this.filters.documentStatusIDs ?? [];

		// 1. Null, empty, or contains -1 → show "All Statuses"
		if (!selectedIds || selectedIds.length === 0 || selectedIds.includes(AllDocumentStatuses.id)) {
			return 'All Statuses';
		} else {
			return this.documentStatusesFlat
				.filter((s) => selectedIds.includes(s.id))
				.map((s) => s.name)
				.join(' • ');
		}
	}

	// Toggle all documents in a group
	onToggleGroup(group: GroupedDocumentType) {
		if (group.groupId === AllDocumentTypes.groupId) {
			if (group.selected) return; // Already selected

			// Deselect all groups and documents
			for (const g of this.documentTypesFlat) {
				g.selected = false;
				for (const doc of g.documents) {
					doc.selected = false;
				}
			}

			group.selected = true;
		} else {
			// Deselect "All Document Types"
			AllDocumentTypes.selected = false;

			const allSelected = group.documents.every((doc) => doc.selected);
			const newValue = !allSelected;

			group.selected = newValue;

			for (const doc of group.documents) {
				doc.selected = newValue;
			}
		}

		this.syncSelectedIds();
		this.onLoadApprovals(true);
	}

	// Toggle single document
	onToggleItem(id: number) {
		// Always deselect "All Document Types"
		AllDocumentTypes.selected = false;

		for (const group of this.documentTypesFlat) {
			for (const doc of group.documents) {
				if (doc.id === id) {
					// Toggle selected state for the clicked document
					doc.selected = !doc.selected;
				}
			}

			// Update group.selected: true only if ALL docs in group are selected
			group.selected = group.documents.length > 0 && group.documents.every((d) => d.selected);
		}

		this.syncSelectedIds(); // Updates filters.documentTypeIDs from selection state
		this.onLoadApprovals(true);
	}

	private syncSelectedIds() {
		// If choose "All Document Types" — only it
		if (AllDocumentTypes.selected) {
			this.filters.documentTypeIDs = [AllDocumentTypes.groupId];
			return;
		}

		// Collect all selected document IDs across all groups
		const selectedIds: number[] = [];

		for (const group of this.documentTypesFlat) {
			for (const doc of group.documents) {
				if (doc.selected) {
					selectedIds.push(doc.id);
				}
			}
		}

		this.filters.documentTypeIDs = selectedIds;
	}

	// Label in collapsed select
	documentTypesLabel(): string {
		// 1. If "All Document Types" is selected, return this label immediately
		if (AllDocumentTypes.selected) {
			return 'All Document Types';
		}

		const parts: string[] = [];

		for (const group of this.documentTypesFlat) {
			// Skip the synthetic "All Document Types" group
			if (group.groupId === AllDocumentTypes.groupId) continue;

			if (group.selected) {
				// 2. If the whole group is selected, display only the group label
				parts.push(group.groupLabel);
			} else {
				// 3. If only some documents in the group are selected, list their names
				const selectedDocs = group.documents
					.filter((doc) => doc.selected)
					.map((doc) => doc.name);

				if (selectedDocs.length > 0) {
					parts.push(
						`${group.groupLabel}: ${selectedDocs.join(', ')}`,
					);
				}
			}
		}

		// 4. If nothing is selected (edge case), return "All Document Types"
		if (parts.length === 0) {
			return 'All Document Types';
		}

		// 5. Join all parts with a separator
		return parts.join(' • ');
	}

	async onSortChange(sortField: string) {
		this.filters.sortField = sortField;
		await this.onLoadApprovals(true);
	}
}
